import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreVertOutlined,
  PrintOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import { TransferActionType } from "../../constants"
import {
  isTransferAdjustmentUpsertFailed,
  isTransferAdjustmentUpserted,
  isTransferEditable,
  isTransferWorkOrderUpserted,
} from "../../utils/transfer"

import { type Transfer, TransferAs400SyncStatus, TransferStatus, TransferType } from "@/graphql/codegen/graphql"

const TRANSFER_TYPE_TO_STATUS_ACTIONS: Record<TransferType, Partial<Record<TransferStatus, TransferActionType[]>>> = {
  [TransferType.IntraWarehouse]: {
    [TransferStatus.Draft]: [TransferActionType.Edit, TransferActionType.Cancel],
    [TransferStatus.PendingPickAndPutaway]: [
      TransferActionType.Edit,
      TransferActionType.WorkOrderConfirm,
      TransferActionType.WorkOrderPrint,
      TransferActionType.Cancel,
      TransferActionType.PalletLabelPrint,
    ],
    [TransferStatus.Completed]: [TransferActionType.PalletLabelPrint],
    [TransferStatus.Cancelled]: [],
  },
  [TransferType.InterWarehouse]: {
    [TransferStatus.Draft]: [TransferActionType.Edit, TransferActionType.LpnEdit, TransferActionType.Cancel],
    // AK-6100 AC7: Pick ticket printing is only for `PendingPick` (incl. pending sync to AS400).
    // AK-6098 AC7: Editing, LPN editing and cancelling only apply while `PendingPick` if its adjustment creation failed.
    [TransferStatus.PendingPick]: [
      TransferActionType.Edit,
      TransferActionType.LpnEdit,
      TransferActionType.PickTicketPrint,
      TransferActionType.ConfirmPick,
      TransferActionType.Cancel,
      TransferActionType.PalletLabelPrint,
    ],
    // AK-6102 AC1: The laid down cost is editable from `PendingShip` onwards.
    [TransferStatus.PendingShip]: [TransferActionType.LaidDownCostEdit, TransferActionType.PalletLabelPrint],
    // TODO(AK-6103): Disable receiving confirmation while `InTransit` + pending sync to AS400 (AC3).
    [TransferStatus.InTransit]: [
      TransferActionType.ConfirmReceived,
      TransferActionType.LaidDownCostEdit,
      TransferActionType.PalletLabelPrint,
    ],
    [TransferStatus.Received]: [TransferActionType.LaidDownCostEdit, TransferActionType.PalletLabelPrint],
    [TransferStatus.Completed]: [TransferActionType.LaidDownCostEdit, TransferActionType.PalletLabelPrint],
    [TransferStatus.Cancelled]: [],
  },
}

const AS400_SYNC_PENDING_STATUSES = new Set<TransferAs400SyncStatus>([
  TransferAs400SyncStatus.AdjustmentUpserting,
  TransferAs400SyncStatus.AdjustmentDeleting,
  TransferAs400SyncStatus.ConfirmPickSyncing,
  TransferAs400SyncStatus.WorkOrderUpserting,
  TransferAs400SyncStatus.WorkOrderConfirming,
  TransferAs400SyncStatus.WorkOrderDeleting,
])

interface TransferRowActionsProps {
  transfer: Transfer
  isPrinting: boolean
  onEdit: (transfer: Transfer) => void
  onLpnEdit: (transfer: Transfer) => void
  onLaidDownCostEdit: (transfer: Transfer) => void
  onReceivedConfirm: (transfer: Transfer) => void
  onCancel: (transfer: Transfer) => void
  onWorkOrderConfirm: (transfer: Transfer) => void
  onWorkOrderPrint: (transfer: Transfer) => void
  onPickTicketPrint: (transfer: Transfer) => void
  onPickConfirm: (transfer: Transfer) => void
  onPalletLabelPrintAll: (transfer: Transfer) => void
}

export const TransferRowActions: FC<TransferRowActionsProps> = ({
  transfer,
  isPrinting,
  onEdit,
  onLpnEdit,
  onLaidDownCostEdit,
  onReceivedConfirm,
  onCancel,
  onWorkOrderConfirm,
  onWorkOrderPrint,
  onPickTicketPrint,
  onPickConfirm,
  onPalletLabelPrintAll,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const actions = TRANSFER_TYPE_TO_STATUS_ACTIONS[transfer.transferType][transfer.status] ?? []
  const canEdit = isTransferEditable(transfer)
  const isSyncPending = !!transfer.as400SyncStatus && AS400_SYNC_PENDING_STATUSES.has(transfer.as400SyncStatus)
  const hasWorkOrder = isTransferWorkOrderUpserted(transfer)
  const canPrintLabel = hasWorkOrder || isTransferAdjustmentUpserted(transfer)
  // A failed cancel of an inter-warehouse transfer (`AdjustmentDeleteFailed`) can be retried even though the transfer can't be edited.
  const canCancel = canEdit || transfer.as400SyncStatus === TransferAs400SyncStatus.AdjustmentDeleteFailed
  const hasPallets = transfer.products.some(({ pallets }) => pallets.length > 0)

  const handleMenuClose = () => setIsMenuOpen(false)

  const handleWorkOrderPrint = () => {
    handleMenuClose()
    onWorkOrderPrint(transfer)
  }

  const handlePickTicketPrint = () => {
    handleMenuClose()
    onPickTicketPrint(transfer)
  }

  const handlePickConfirm = () => {
    handleMenuClose()
    onPickConfirm(transfer)
  }

  const handleEdit = () => {
    handleMenuClose()
    onEdit(transfer)
  }

  const handleLpnEdit = () => {
    handleMenuClose()
    onLpnEdit(transfer)
  }

  const handleLaidDownCostEdit = () => {
    handleMenuClose()
    onLaidDownCostEdit(transfer)
  }

  const handleReceivedConfirm = () => {
    handleMenuClose()
    onReceivedConfirm(transfer)
  }

  const handleCancel = () => {
    handleMenuClose()
    onCancel(transfer)
  }

  const handleWorkOrderConfirm = () => {
    handleMenuClose()
    onWorkOrderConfirm(transfer)
  }

  const handlePalletLabelPrintAll = () => {
    handleMenuClose()
    onPalletLabelPrintAll(transfer)
  }

  if (!actions.length) return null

  return (
    <Box ref={anchorElRef}>
      <IconButton size='small' onClick={() => setIsMenuOpen(true)} data-testid='transfer.row-actions.menu-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='transfer.row-actions.menu'
      >
        {actions.includes(TransferActionType.Edit) && canEdit && (
          <MenuItem onClick={handleEdit} disabled={isSyncPending} data-testid='transfer.row-actions.edit-menu-item'>
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            <ListItemText primary='Edit Transfer' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.LpnEdit) && canEdit && (
          <MenuItem
            onClick={handleLpnEdit}
            disabled={isSyncPending || !hasPallets}
            data-testid='transfer.row-actions.edit-lpn-menu-item'
          >
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            <ListItemText primary='Edit LPN' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.PickTicketPrint) && !isTransferAdjustmentUpsertFailed(transfer) && (
          <MenuItem onClick={handlePickTicketPrint} data-testid='transfer.row-actions.view-pick-ticket-menu-item'>
            <ListItemIcon>
              <VisibilityOutlined />
            </ListItemIcon>
            <ListItemText primary='View Pick Ticket' />
          </MenuItem>
        )}
        {/* AK-6821 AC9: A failed confirm pick can be retried; a failed creation can't, since there is nothing to confirm. */}
        {actions.includes(TransferActionType.ConfirmPick) && !isTransferAdjustmentUpsertFailed(transfer) && (
          <MenuItem
            onClick={handlePickConfirm}
            disabled={isSyncPending}
            data-testid='transfer.row-actions.confirm-pick-menu-item'
          >
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Confirm Pick' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.LaidDownCostEdit) && (
          <MenuItem onClick={handleLaidDownCostEdit} data-testid='transfer.row-actions.edit-laid-down-cost-menu-item'>
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            <ListItemText primary='View / Edit Extended Laid Down Cost' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.ConfirmReceived) && (
          <MenuItem
            onClick={handleReceivedConfirm}
            disabled={isSyncPending}
            data-testid='transfer.row-actions.confirm-received-menu-item'
          >
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Confirm Received' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.WorkOrderConfirm) && hasWorkOrder && (
          <MenuItem
            onClick={handleWorkOrderConfirm}
            disabled={isSyncPending}
            data-testid='transfer.row-actions.confirm-work-order-menu-item'
          >
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Confirm Work Order' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.WorkOrderPrint) && hasWorkOrder && (
          <MenuItem onClick={handleWorkOrderPrint} data-testid='transfer.row-actions.print-work-order-menu-item'>
            <ListItemIcon>
              <PrintOutlined />
            </ListItemIcon>
            <ListItemText primary='Print Work Order' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.Cancel) && canCancel && (
          <MenuItem onClick={handleCancel} disabled={isSyncPending} data-testid='transfer.row-actions.cancel-menu-item'>
            <ListItemIcon>
              <DeleteOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel Transfer' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.PalletLabelPrint) && canPrintLabel && (
          <>
            {actions.length > 1 && <Divider />}
            <MenuItem
              onClick={handlePalletLabelPrintAll}
              disabled={isPrinting}
              data-testid='transfer.row-actions.print-pallet-label-menu-item'
            >
              <ListItemIcon>
                <PrintOutlined />
              </ListItemIcon>
              <ListItemText primary='Print All QR Codes' />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  )
}
